// src/modules/ida/components/DocumentPreviewModal.tsx
//
// What? Modal for previewing document files (HTML, PDF, images, markdown, text).
// Why? Allows in-app preview without downloading, especially for HTML artifacts.
// How? Fetches raw bytes via download endpoint, renders based on file type.

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Modal,
  ModalDialog,
  Tooltip,
  Alert,
  Switch,
  FormControl,
  FormLabel,
  Stack,
  Input,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/joy";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockIcon from "@mui/icons-material/Lock";
import WarningIcon from "@mui/icons-material/Warning";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { IDADocument } from "../common/types";
import {
  getPreviewType,
  getExtension,
  extractRelativeAssetFilenames,
  processHtmlWithAssets,
} from "../common/htmlPreview";
import { useMMDataBridge } from "../hooks/useMMDataBridge";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import {
  useDownloadDocument,
  useResolveAssets,
  useGetDocumentAccess,
  useUpdateDocumentPublish,
  useUpdateDocumentPublic,
  useUpdateDocumentPassword,
  type DocumentAccessInfo,
} from "../services/api";
import { buildPublishedDocumentUrl } from "../utils/publicLinks";

interface DocumentPreviewModalProps {
  open: boolean;
  document: IDADocument | null;
  workspaceId: string;
  onClose: () => void;
  /** Preview an earlier SharePoint version of a SharePoint-backed document. */
  spVersionId?: string;
}

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  open,
  document: doc,
  workspaceId,
  onClose,
  spVersionId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contentUrl, setContentUrl] = useState<string | null>(null);
  const [contentOrigin, setContentOrigin] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Publishing state
  const getDocumentAccess = useGetDocumentAccess();
  const updateDocumentPublish = useUpdateDocumentPublish();
  const updateDocumentPublic = useUpdateDocumentPublic();
  const [accessInfo, setAccessInfo] = useState<DocumentAccessInfo | null>(null);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishFeedback, setPublishFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmPublicOpen, setConfirmPublicOpen] = useState(false);

  const updateDocumentPassword = useUpdateDocumentPassword();
  const [passwordEditing, setPasswordEditing] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const PASSWORD_MIN_LENGTH = 12;

  const authFetch = useAuthFetch("raw");
  const downloadDocument = useDownloadDocument();
  const resolveAssets = useResolveAssets();
  const contentUrlRef = useRef<string | null>(null);

  // mmData bridge: relay data mutations from iframe to backend
  const getAuthHeaders = useCallback((): Record<string, string> => {
    // Read cached token from localStorage (works for both Auth0 and MSAL)
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (key.startsWith("@@auth0spajs@@")) {
          const parsed = JSON.parse(localStorage.getItem(key) || "");
          const token = parsed?.body?.access_token;
          if (token) return { Authorization: `Bearer ${token}` };
        }
        if (key.toLowerCase().includes("-accesstoken-")) {
          const parsed = JSON.parse(localStorage.getItem(key) || "");
          const target = (parsed?.target || "").toLowerCase();
          if (!target.includes("access_as_user")) continue;
          const token = parsed?.secret;
          if (token) return { Authorization: `Bearer ${token}` };
        }
      }
    } catch {
      /* ignore */
    }
    return {};
  }, []);
  const iframeRef = useMMDataBridge({
    documentId: doc?.id,
    contentOrigin,
    getAuthHeaders,
  });

  // Revoke previous blob URL
  const revokeContentUrl = useCallback(() => {
    if (contentUrlRef.current) {
      URL.revokeObjectURL(contentUrlRef.current);
      contentUrlRef.current = null;
    }
  }, []);

  // Fetch and prepare content when modal opens
  useEffect(() => {
    if (!open || !doc || !workspaceId) {
      return;
    }

    const previewType = getPreviewType(doc.filename);
    if (previewType === "unsupported") {
      setError("This file type cannot be previewed.");
      return;
    }

    let cancelled = false;

    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      setContentUrl(null);
      setContentOrigin(null);
      setTextContent(null);
      revokeContentUrl();

      try {
        const encodedDocId = encodeURIComponent(doc.id);
        if (previewType === "html") {
          const sessionResponse = await authFetch(
            `/api/workspace/ida/documents/${encodedDocId}/content-session?workspace_id=${encodeURIComponent(workspaceId)}`,
            "create HTML preview content session",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ app_origin: window.location.origin }),
            },
          );
          const session = (await sessionResponse.json()) as {
            configured?: boolean;
            url?: string;
            origin?: string;
          };
          if (session.configured && session.url && session.origin) {
            if (!cancelled) {
              setContentOrigin(session.origin);
              setContentUrl(session.url);
            }
            return;
          }
          // The legacy inline preview remains only in lanes without a content host.
          setContentOrigin(null);
        }
        const versionParam = spVersionId
          ? `&sp_version_id=${encodeURIComponent(spVersionId)}`
          : "";
        const response = await authFetch(
          `/api/workspace/ida/documents/download/${encodedDocId}?workspace_id=${workspaceId}${versionParam}`,
          `preview document ${doc.filename}`,
          {},
          60000,
        );

        if (cancelled) return;

        const blob = await response.blob();

        if (cancelled) return;

        if (previewType === "text" || previewType === "markdown") {
          const text = await blob.text();
          if (!cancelled) {
            // Pretty-print JSON
            if (getExtension(doc.filename) === "json") {
              try {
                const parsed = JSON.parse(text);
                setTextContent(JSON.stringify(parsed, null, 2));
              } catch {
                setTextContent(text);
              }
            } else {
              setTextContent(text);
            }
          }
        } else if (previewType === "html") {
          // HTML: read as text, resolve relative asset references, then create blob URL
          let html = await blob.text();
          if (cancelled) return;

          const filenames = extractRelativeAssetFilenames(html);
          let assets = {};
          if (filenames.length > 0) {
            try {
              assets = await resolveAssets(workspaceId, filenames, doc?.folder);
            } catch (err) {
              // Non-fatal: preview still works, just without linked assets
              console.warn("Failed to resolve linked assets:", err);
            }
          }
          if (cancelled) return;
          html = processHtmlWithAssets(html, assets);

          if (!cancelled) {
            const resolvedBlob = new Blob([html], { type: "text/html" });
            const url = URL.createObjectURL(resolvedBlob);
            contentUrlRef.current = url;
            setContentUrl(url);
          }
        } else {
          // pdf, image -> blob URL
          const url = URL.createObjectURL(blob);
          if (!cancelled) {
            contentUrlRef.current = url;
            setContentUrl(url);
          } else {
            URL.revokeObjectURL(url);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load preview:", err);
          setError(`Failed to load preview: ${(err as Error).message}`);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchContent();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    doc,
    workspaceId,
    spVersionId,
    authFetch,
    resolveAssets,
    revokeContentUrl,
  ]);

  // Cleanup blob URL on unmount or close
  useEffect(() => {
    if (!open) {
      revokeContentUrl();
      setContentUrl(null);
      setContentOrigin(null);
      setTextContent(null);
      setError(null);
      setIsFullscreen(false);
    }
  }, [open, revokeContentUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      revokeContentUrl();
    };
  }, [revokeContentUrl]);

  // Load publish/access state
  useEffect(() => {
    if (open && doc && workspaceId) {
      getDocumentAccess(workspaceId, doc.id)
        .then(setAccessInfo)
        .catch(() => setAccessInfo(null));
    }
    if (!open) {
      setAccessInfo(null);
      setPublishFeedback(null);
      setCopied(false);
    }
  }, [open, doc, workspaceId, getDocumentAccess]);

  const handlePublishToggle = useCallback(
    async (checked: boolean) => {
      if (!doc || !workspaceId) return;
      setPublishLoading(true);
      setPublishFeedback(null);
      try {
        const info = await updateDocumentPublish(workspaceId, doc.id, checked);
        setAccessInfo(info);
        setPublishFeedback({
          type: "success",
          message: checked ? "Document published" : "Document set to private",
        });
      } catch {
        setPublishFeedback({ type: "error", message: "Failed to update" });
      } finally {
        setPublishLoading(false);
        setTimeout(() => setPublishFeedback(null), 3000);
      }
    },
    [doc, workspaceId, updateDocumentPublish],
  );

  const handlePublicToggle = useCallback(
    async (checked: boolean) => {
      if (!doc || !workspaceId) return;
      setPublishLoading(true);
      setPublishFeedback(null);
      try {
        const info = await updateDocumentPublic(workspaceId, doc.id, checked);
        setAccessInfo(info);
        setPublishFeedback({
          type: "success",
          message: checked ? "Document is now public" : "Public access revoked",
        });
      } catch {
        setPublishFeedback({ type: "error", message: "Failed to update" });
      } finally {
        setPublishLoading(false);
        setTimeout(() => setPublishFeedback(null), 3000);
      }
    },
    [doc, workspaceId, updateDocumentPublic],
  );

  const handlePasswordSave = useCallback(async () => {
    if (!doc || !accessInfo) return;
    if (passwordInput.length < PASSWORD_MIN_LENGTH) {
      setPasswordError(
        `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
      );
      return;
    }
    setPasswordError(null);
    setPasswordSaving(true);
    try {
      const info = await updateDocumentPassword(
        workspaceId,
        doc.id,
        passwordInput,
      );
      setAccessInfo(info);
      setPasswordEditing(false);
      setPasswordInput("");
      setPublishFeedback({ type: "success", message: "Password set" });
    } catch (err) {
      setPasswordError(
        err instanceof Error ? err.message : "Failed to set password",
      );
    } finally {
      setPasswordSaving(false);
    }
  }, [doc, accessInfo, passwordInput, updateDocumentPassword, workspaceId]);

  const handlePasswordRemove = useCallback(async () => {
    if (!doc || !accessInfo) return;
    setPasswordSaving(true);
    try {
      const info = await updateDocumentPassword(workspaceId, doc.id, null);
      setAccessInfo(info);
      setPasswordEditing(false);
      setPasswordInput("");
      setPublishFeedback({ type: "success", message: "Password removed" });
    } catch (err) {
      setPasswordError(
        err instanceof Error ? err.message : "Failed to remove password",
      );
    } finally {
      setPasswordSaving(false);
    }
  }, [doc, accessInfo, updateDocumentPassword, workspaceId]);

  const publicUrl = accessInfo?.is_published
    ? buildPublishedDocumentUrl(
        window.location.origin,
        accessInfo.public_doc_id,
      )
    : null;

  const handleCopyPublicUrl = useCallback(async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = globalThis.document.createElement("textarea");
      textArea.value = publicUrl;
      globalThis.document.body.appendChild(textArea);
      textArea.select();
      globalThis.document.execCommand("copy");
      globalThis.document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [publicUrl]);

  const handleDownload = async () => {
    if (!doc || !workspaceId) return;
    try {
      await downloadDocument(workspaceId, doc.id, doc.filename);
    } catch (err) {
      console.error("Failed to download:", err);
    }
  };

  if (!doc) return null;

  const previewType = getPreviewType(doc.filename);
  const ext = getExtension(doc.filename);

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
            p: 3,
          }}
        >
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        </Box>
      );
    }

    switch (previewType) {
      case "html":
        return contentUrl ? (
          <iframe
            ref={iframeRef}
            src={contentUrl}
            sandbox="allow-scripts allow-same-origin"
            title={doc.filename}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              flex: 1,
            }}
          />
        ) : null;

      case "pdf":
        return contentUrl ? (
          <iframe
            src={contentUrl}
            title={doc.filename}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              flex: 1,
            }}
          />
        ) : null;

      case "image":
        return contentUrl ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
              overflow: "auto",
              p: 2,
            }}
          >
            <img
              src={contentUrl}
              alt={doc.filename}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        ) : null;

      case "markdown":
        return textContent !== null ? (
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 3,
              fontSize: "0.875rem",
              lineHeight: 1.8,
              "& p": { my: 1 },
              "& h1": { mt: 2, mb: 1, fontSize: "1.25rem", fontWeight: 600 },
              "& h2": { mt: 2, mb: 1, fontSize: "1.1rem", fontWeight: 600 },
              "& h3": { mt: 1.5, mb: 0.5, fontSize: "1rem", fontWeight: 600 },
              "& ul, & ol": { pl: 2, my: 1 },
              "& li": { mb: 0.5 },
              "& code": {
                bgcolor: "neutral.100",
                px: 0.5,
                py: 0.25,
                borderRadius: "sm",
                fontSize: "0.85em",
              },
              "& pre": {
                bgcolor: "neutral.100",
                p: 1.5,
                borderRadius: "sm",
                overflow: "auto",
                "& code": { bgcolor: "transparent", p: 0 },
              },
              "& blockquote": {
                borderLeft: "3px solid",
                borderColor: "neutral.300",
                pl: 2,
                ml: 0,
                fontStyle: "italic",
                color: "text.secondary",
              },
              "& table": {
                borderCollapse: "collapse",
                width: "100%",
                my: 1,
              },
              "& th, & td": {
                border: "1px solid",
                borderColor: "neutral.300",
                px: 1,
                py: 0.5,
                textAlign: "left",
              },
              "& th": {
                bgcolor: "neutral.100",
                fontWeight: 600,
              },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {textContent}
            </ReactMarkdown>
          </Box>
        ) : null;

      case "text":
        return textContent !== null ? (
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 2,
            }}
          >
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily:
                  "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                fontSize: "0.8rem",
                lineHeight: 1.6,
              }}
            >
              {textContent}
            </pre>
          </Box>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalDialog
          sx={{
            width: isFullscreen ? "100vw" : "90vw",
            height: isFullscreen ? "100vh" : "85vh",
            maxWidth: isFullscreen ? "100vw" : "1200px",
            maxHeight: isFullscreen ? "100vh" : "85vh",
            borderRadius: isFullscreen ? 0 : undefined,
            p: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: "all 0.2s ease",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography level="title-md" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {doc.filename}
            </Typography>
            <Chip size="sm" variant="soft" color="neutral">
              {ext.toUpperCase()}
            </Chip>
            <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => setIsFullscreen((prev) => !prev)}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={handleDownload}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Publishing controls */}
          {accessInfo && accessInfo.can_publish && (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.level1",
                flexShrink: 0,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Tooltip title="Make this document available to all workspace members">
                  <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                    <FormLabel sx={{ fontSize: "0.8rem" }}>Publish</FormLabel>
                    <Switch
                      size="sm"
                      checked={accessInfo.is_published}
                      onChange={(e) => handlePublishToggle(e.target.checked)}
                      disabled={publishLoading}
                      color={accessInfo.is_published ? "primary" : "neutral"}
                    />
                  </FormControl>
                </Tooltip>

                {!accessInfo.is_published && (
                  <Chip
                    size="sm"
                    variant="soft"
                    color="neutral"
                    startDecorator={<LockIcon sx={{ fontSize: 14 }} />}
                  >
                    Private
                  </Chip>
                )}

                {accessInfo.is_published && (
                  <Tooltip title="A public document can be viewed by anyone with the address without signing in">
                    <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                      <FormLabel sx={{ fontSize: "0.8rem" }}>Public</FormLabel>
                      <Switch
                        size="sm"
                        checked={accessInfo.is_public}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setConfirmPublicOpen(true);
                          } else {
                            handlePublicToggle(false);
                          }
                        }}
                        disabled={publishLoading}
                        color={accessInfo.is_public ? "success" : "neutral"}
                      />
                    </FormControl>
                  </Tooltip>
                )}

                {accessInfo.is_published && (
                  <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                    <Tooltip title="Require recipients to enter a password before viewing">
                      <FormLabel sx={{ fontSize: "0.8rem" }}>
                        Password
                      </FormLabel>
                    </Tooltip>
                    <Switch
                      size="sm"
                      checked={accessInfo.password_protected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPasswordEditing(true);
                          setPasswordError(null);
                        } else {
                          handlePasswordRemove();
                        }
                      }}
                      disabled={passwordSaving}
                      color={
                        accessInfo.password_protected ? "warning" : "neutral"
                      }
                    />
                    {accessInfo.password_protected && !passwordEditing && (
                      <Button
                        size="sm"
                        variant="plain"
                        onClick={() => {
                          setPasswordEditing(true);
                          setPasswordError(null);
                        }}
                      >
                        Change
                      </Button>
                    )}
                  </FormControl>
                )}

                {publishLoading && <CircularProgress size="sm" />}

                {publicUrl && (
                  <>
                    <Input
                      size="sm"
                      readOnly
                      value={publicUrl}
                      sx={{ flex: 1, fontSize: "0.75rem", minWidth: 120 }}
                    />
                    <Tooltip title={copied ? "Copied!" : "Copy link"}>
                      <IconButton
                        size="sm"
                        variant="soft"
                        color={copied ? "success" : "neutral"}
                        onClick={handleCopyPublicUrl}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    {accessInfo.password_protected && (
                      <Chip
                        size="sm"
                        variant="soft"
                        color="warning"
                        startDecorator={<LockIcon sx={{ fontSize: 14 }} />}
                      >
                        Password required
                      </Chip>
                    )}
                  </>
                )}
              </Stack>

              {passwordEditing && (
                <Box sx={{ mt: 1, pl: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Input
                      size="sm"
                      type="password"
                      value={passwordInput}
                      onChange={(e) => {
                        setPasswordInput(e.target.value);
                        setPasswordError(null);
                      }}
                      placeholder={`Min ${PASSWORD_MIN_LENGTH} characters`}
                      autoFocus
                      sx={{ flex: 1, minWidth: 200 }}
                      // NIST SP 800-63B: allow paste
                      slotProps={{ input: { autoComplete: "new-password" } }}
                    />
                    <Button
                      size="sm"
                      onClick={handlePasswordSave}
                      disabled={
                        passwordSaving ||
                        passwordInput.length < PASSWORD_MIN_LENGTH
                      }
                    >
                      {passwordSaving ? <CircularProgress size="sm" /> : "Save"}
                    </Button>
                    <Button
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={() => {
                        setPasswordEditing(false);
                        setPasswordInput("");
                        setPasswordError(null);
                      }}
                      disabled={passwordSaving}
                    >
                      Cancel
                    </Button>
                  </Stack>
                  {passwordError && (
                    <Alert
                      color="danger"
                      size="sm"
                      sx={{ mt: 1 }}
                      startDecorator={<WarningIcon />}
                    >
                      {passwordError}
                    </Alert>
                  )}
                </Box>
              )}

              {publishFeedback && (
                <Alert
                  size="sm"
                  color={
                    publishFeedback.type === "success" ? "success" : "danger"
                  }
                  variant="soft"
                  sx={{ mt: 0.5 }}
                >
                  {publishFeedback.message}
                </Alert>
              )}
            </Box>
          )}

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {renderContent()}
          </Box>
        </ModalDialog>
      </Modal>

      {/* Confirmation modal for making document public */}
      <Modal
        open={confirmPublicOpen}
        onClose={() => setConfirmPublicOpen(false)}
      >
        <ModalDialog
          variant="outlined"
          role="alertdialog"
          sx={{ maxWidth: 420 }}
        >
          <DialogTitle>
            <WarningIcon color="warning" />
            Make document public?
          </DialogTitle>
          <Divider />
          <DialogContent>
            This will make the document accessible to anyone with the link,
            without requiring sign-in. Are you sure?
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="warning"
              onClick={() => {
                setConfirmPublicOpen(false);
                handlePublicToggle(true);
              }}
            >
              Make public
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setConfirmPublicOpen(false)}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default DocumentPreviewModal;
